import type { RefCallback } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { ListLoadingStates } from '@frameio/vapor';

type UseResultsListFocusOnTabChangeArgs = {
  visibleItemsCount: number;
  visibleLoadingStatus: ListLoadingStates | undefined;
};

type UseResultsListFocusOnTabChangeResult = {
  scheduleResultsListFocus: () => void;
  resultsListRef: RefCallback<HTMLDivElement>;
};

function shouldClearPendingFocus({
  visibleItemsCount,
  visibleLoadingStatus,
}: UseResultsListFocusOnTabChangeArgs): boolean {
  if (visibleLoadingStatus === 'loading-error') {
    return true;
  }

  return (
    visibleItemsCount === 0 &&
    visibleLoadingStatus !== 'loading' &&
    visibleLoadingStatus !== 'loading-more'
  );
}

export function useResultsListFocusOnTabChange({
  visibleItemsCount,
  visibleLoadingStatus,
}: UseResultsListFocusOnTabChangeArgs): UseResultsListFocusOnTabChangeResult {
  const resultsListNodeRef = useRef<HTMLDivElement | null>(null);
  const pendingResultsFocusRef = useRef(false);
  const resultsFocusFrameRef = useRef<number | null>(null);

  const cancelScheduledFocus = useCallback(() => {
    if (resultsFocusFrameRef.current !== null) {
      cancelAnimationFrame(resultsFocusFrameRef.current);
      resultsFocusFrameRef.current = null;
    }
  }, []);

  const clearPendingFocus = useCallback(() => {
    pendingResultsFocusRef.current = false;
    cancelScheduledFocus();
  }, [cancelScheduledFocus]);

  const scheduleResultsFocus = useCallback(() => {
    cancelScheduledFocus();

    resultsFocusFrameRef.current = requestAnimationFrame(() => {
      resultsFocusFrameRef.current = null;

      if (!pendingResultsFocusRef.current || !resultsListNodeRef.current) {
        return;
      }

      resultsListNodeRef.current.focus({ preventScroll: true });
      pendingResultsFocusRef.current = false;
    });
  }, [cancelScheduledFocus]);

  const resultsListRef = useCallback(
    (node: HTMLDivElement | null) => {
      resultsListNodeRef.current = node;

      if (node && pendingResultsFocusRef.current) {
        scheduleResultsFocus();
      }
    },
    [scheduleResultsFocus]
  );

  const scheduleResultsListFocus = useCallback(() => {
    pendingResultsFocusRef.current = true;
    scheduleResultsFocus();
  }, [scheduleResultsFocus]);

  useEffect(() => {
    if (
      pendingResultsFocusRef.current &&
      shouldClearPendingFocus({ visibleItemsCount, visibleLoadingStatus })
    ) {
      clearPendingFocus();
    }
  }, [clearPendingFocus, visibleItemsCount, visibleLoadingStatus]);

  useEffect(() => {
    return () => {
      clearPendingFocus();
      resultsListNodeRef.current = null;
    };
  }, [clearPendingFocus]);

  return { resultsListRef, scheduleResultsListFocus };
}
